'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import PropTypes from 'prop-types';
import { DEFAULT_COMPETITION_QUESTION } from '@/lib/legal';

// The competition question. The page only gets the question and its options:
// the answer stays on the server, which checks it when the tickets go into
// the basket (and says if it's wrong).
const PreCartQuestionnaire = ({ onComplete, competition, error = '' }) => {
  const [answers, setAnswers] = useState({});

  // Question and options (no answer). The default question is used when a
  // competition has none of its own; the server uses the same default.
  const parseQuestionData = () => {
    let questionData = competition?.generalKnowledgeQuestion;

    if (typeof questionData === 'string') {
      try {
        questionData = JSON.parse(questionData);
      } catch (err) {
        questionData = null;
      }
    }

    if (!questionData?.question || !Array.isArray(questionData?.options) || questionData.options.length === 0) {
      questionData = DEFAULT_COMPETITION_QUESTION;
    }

    return {
      question: questionData.question,
      options: questionData.options.map(o =>
        typeof o === 'string'
          ? { value: o, label: o }
          : { value: String(o?.value ?? o?.label ?? ''), label: String(o?.label ?? o?.value ?? '') }
      )
    };
  };

  const questionData = parseQuestionData();

  const questions = [
    {
      id: 'general_knowledge',
      type: 'radio',
      question: questionData.question,
      required: true,
      options: questionData.options
    }
  ];

  const handleAnswerChange = (questionId, value) => {
    const next = { ...answers, [questionId]: value };
    setAnswers(next);
    onComplete(next);
  };

  const renderQuestion = (question) => {
    const hasError = question?.id === 'general_knowledge' ? error : null;

    return (
      <div key={question?.id} className="space-y-3">
        <label className="text-sm font-medium text-foreground flex items-center">
          {question?.question}
          {question?.required && <span className="text-destructive ml-1">*</span>}
        </label>
        <div className="space-y-2">
          {question?.options?.map((option) => (
            <label
              key={option?.value}
              className={`flex items-center space-x-3 p-3 rounded-[6px] border cursor-pointer transition-smooth ${
                answers?.[question?.id] === option?.value
                  ? 'border-accent bg-accent/10 text-accent'
                  : hasError
                  ? 'border-destructive bg-destructive/5 hover:border-destructive/50' :'border-border bg-card hover:border-accent/50'
              }`}
            >
              <input
                type="radio"
                name={question?.id}
                value={option?.value}
                checked={answers?.[question?.id] === option?.value}
                onChange={(e) => handleAnswerChange(question?.id, e?.target?.value)}
                className="w-4 h-4 text-accent bg-input border-border focus:ring-accent focus:ring-2"
              />
              <span className="text-sm">{option?.label}</span>
            </label>
          ))}
        </div>
        {hasError && (
          <div className="flex items-center space-x-2 text-destructive text-sm" role="alert">
            <Icon name="ExclamationTriangleIcon" size={16} />
            <span>{hasError}</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div 
      id="questionnaire-section"
      className="bg-card rounded-[6px] border border-border p-4 mb-4"
    >
      <div className="flex items-center space-x-2 mb-3">
        <Icon name="QuestionMarkCircleIcon" size={18} className="text-accent" />
        <h4 className="font-medium text-foreground">General Knowledge Question</h4>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        Please answer this question before adding tickets to your cart.
      </p>
      <div className="space-y-4">
        {questions?.map(renderQuestion)}
      </div>
      <div className="mt-3 p-2 bg-muted/30 rounded-[6px]">
        <div className="flex items-start space-x-2">
          <Icon name="ShieldCheckIcon" size={14} className="text-accent mt-0.5" />
          <div className="text-xs text-muted-foreground">
            <p className="font-medium text-foreground mb-1">Why This Question?</p>
            <p>
              This general knowledge question is required for compliance purposes and helps verify participation eligibility.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

PreCartQuestionnaire.propTypes = {
  onComplete: PropTypes?.func?.isRequired,
  competition: PropTypes?.shape({
    id: PropTypes?.oneOfType([PropTypes?.string, PropTypes?.number]),
    title: PropTypes?.string,
    general_knowledge_question: PropTypes?.oneOfType([
      PropTypes?.string,
      PropTypes?.shape({
        question: PropTypes?.string,
        options: PropTypes?.arrayOf(
          PropTypes?.shape({
            value: PropTypes?.string,
            label: PropTypes?.string
          })
        )
      })
    ])
  }),
  // The server's message when the answer is wrong.
  error: PropTypes?.string
};

export default PreCartQuestionnaire;